import type { FC } from 'react';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Add, Edit as EditIcon, Remove } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { toast } from '@/components/core/v2/toaster';
import { z as zod } from 'zod';

import type { Amenity, UpdateAmenityRequest } from '@/types/v2/amenity';
import { updateAmenity } from '@/api/amenities';
import { ProfessionalDialog } from '@/components/common';
import { useUser } from '@/hooks/v2/use-user';

const schema = zod.object({
  name: zod.string().min(1, 'Name is required'),
  description: zod.string().optional(),
  fee_description: zod.string().optional(),
  deposit_amount: zod.number().min(0).optional(),
  total_fee_amount: zod.number().min(0).optional(),
  rules: zod.string().optional(),
  required_documents: zod.array(zod.string()).optional().default([]),
  requires_approval: zod.boolean().optional(),
  is_active: zod.boolean().optional(),
});

type Values = zod.infer<typeof schema>;

interface EditAmenityDialogProps {
  open: boolean;
  amenity: Amenity;
  onClose: () => void;
  onSuccess: (amenity: Amenity) => void;
}

export const EditAmenityDialog: FC<EditAmenityDialogProps> = ({ open, amenity, onClose, onSuccess }) => {
  const { community } = useUser();
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'required_documents',
  });

  useEffect(() => {
    if (amenity && open) {
      reset({
        name: amenity.name,
        description: amenity.description ?? '',
        fee_description: amenity.fee_description ?? '',
        deposit_amount: amenity.deposit_amount != null ? Number(amenity.deposit_amount) : undefined,
        total_fee_amount: amenity.total_fee_amount != null ? Number(amenity.total_fee_amount) : undefined,
        rules: amenity.rules ?? '',
        required_documents: Array.isArray(amenity.required_documents)
          ? amenity.required_documents.length > 0
            ? [...amenity.required_documents]
            : []
          : [],
        requires_approval: amenity.requires_approval ?? true,
        is_active: amenity.is_active ?? true,
      });
    }
  }, [amenity, open, reset]);

  const mutation = useMutation({
    mutationFn: (data: Partial<UpdateAmenityRequest>) =>
      updateAmenity(community?.id || '', amenity.id, data),
    onSuccess: (updatedAmenity) => {
      queryClient.invalidateQueries({ queryKey: ['amenities', community?.id] });
      toast.success('Amenity updated successfully');
      onSuccess(updatedAmenity);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update amenity');
    },
  });

  const onSubmit = (data: Values) => {
    const required_documents = (data.required_documents ?? [])
      .map((s) => s.trim())
      .filter(Boolean);
    mutation.mutate({
      ...data,
      required_documents: required_documents.length > 0 ? required_documents : undefined,
    });
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <ProfessionalDialog
      icon={<EditIcon sx={{ fontSize: 24, color: 'primary.contrastText' }} />}
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      subtitle={amenity?.name ? `Edit ${amenity.name}` : 'Edit amenity'}
      title="Edit Amenity"
      actions={
        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <LoadingButton disabled={isSubmitting} loading={isSubmitting} type="submit" variant="contained">
              {isSubmitting ? 'Updating...' : 'Update'}
            </LoadingButton>
          </Box>
        </form>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack mt={2} spacing={3}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Amenity Name"
                required
                fullWidth
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                fullWidth
                multiline
                rows={2}
                error={Boolean(errors.description)}
                helperText={errors.description?.message}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />

          <Grid container spacing={2}>
            <Grid xs={12} md={6}>
              <Controller
                name="deposit_amount"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <TextField
                    {...field}
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
                    label="Deposit Amount"
                    type="number"
                    fullWidth
                    error={Boolean(errors.deposit_amount)}
                    helperText={errors.deposit_amount?.message || 'Optional'}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>
            <Grid xs={12} md={6}>
              <Controller
                name="total_fee_amount"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <TextField
                    {...field}
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
                    label="Total Fee Amount"
                    type="number"
                    fullWidth
                    error={Boolean(errors.total_fee_amount)}
                    helperText={errors.total_fee_amount?.message || 'Optional'}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Controller
            name="fee_description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Fee Description"
                fullWidth
                error={Boolean(errors.fee_description)}
                helperText={errors.fee_description?.message}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />

          <Controller
            name="rules"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Rules & Policies"
                fullWidth
                multiline
                rows={3}
                placeholder="Optional"
                error={Boolean(errors.rules)}
                helperText={errors.rules?.message}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Required documents (for deposit)
              </Typography>
              <Button
                type="button"
                size="small"
                startIcon={<Add />}
                onClick={() => append('')}
                sx={{ textTransform: 'none' }}
              >
                Add document
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Residents must upload each of these when paying the deposit.
            </Typography>
            <Stack spacing={1.5}>
              {fields.map((field, index) => (
                <Box key={field.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Controller
                    name={`required_documents.${index}`}
                    control={control}
                    render={({ field: f }) => (
                      <TextField
                        {...f}
                        fullWidth
                        placeholder="e.g. ID, Insurance, Signed agreement"
                        size="small"
                        error={Boolean(errors.required_documents?.[index])}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                  />
                  <IconButton
                    type="button"
                    size="small"
                    onClick={() => remove(index)}
                    aria-label="Remove document"
                    sx={{ mt: 0.5 }}
                  >
                    <Remove fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Controller
              name="requires_approval"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={value ?? true} onChange={(e) => onChange(e.target.checked)} />}
                  label="Requires approval"
                />
              )}
            />
            <Controller
              name="is_active"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={value ?? true} onChange={(e) => onChange(e.target.checked)} />}
                  label="Active"
                />
              )}
            />
          </Box>
        </Stack>
      </form>
    </ProfessionalDialog>
  );
};
